import React, { useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Sticker } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { toast } from "sonner";

const PROD_DEFAULT = "https://novagenixlabs.com.au";

export default function QRPage() {
  // Default the destination to the current origin (preview URL). The user can
  // override with their live production domain before printing stickers.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [url, setUrl] = useState(() => {
    if (origin && !/preview\.emergentagent\.com$/i.test(origin)) return origin;
    return PROD_DEFAULT;
  });
  const [destination, setDestination] = useState("");  // optional path append (e.g. "/shop")
  const [stickerSize, setStickerSize] = useState(1024); // pixels
  const previewRef = useRef(null);

  const finalUrl = useMemo(() => {
    const base = url.replace(/\/+$/, "");
    const path = destination.startsWith("/") ? destination : destination ? `/${destination}` : "";
    return `${base}${path}`;
  }, [url, destination]);

  /** Download just the QR code (transparent / large for print). */
  const downloadQR = (px = 1536) => {
    // Render an off-screen high-res canvas so the print result is crisp.
    const wrap = document.createElement("div");
    wrap.style.position = "fixed";
    wrap.style.left = "-9999px";
    document.body.appendChild(wrap);
    // Use a temporary canvas via the same QRCodeCanvas — we'll re-render via offscreen.
    // Simpler: scale the visible canvas up by stamping its data into a bigger canvas.
    const src = document.getElementById("ngx-qr");
    if (!src) { document.body.removeChild(wrap); return; }
    const out = document.createElement("canvas");
    out.width = px;
    out.height = px;
    const ctx = out.getContext("2d");
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, px, px);
    ctx.drawImage(src, 0, 0, px, px);
    const link = document.createElement("a");
    link.download = "novagenix-qr-code.png";
    link.href = out.toDataURL("image/png");
    link.click();
    document.body.removeChild(wrap);
    toast.success("QR PNG downloaded");
  };

  /** Download a sticker-ready layout: QR + brand text + URL below. */
  const downloadSticker = () => {
    const src = document.getElementById("ngx-qr");
    if (!src) return;
    const size = stickerSize;
    const padding = Math.round(size * 0.08);
    const qrSize = Math.round(size * 0.72);
    const out = document.createElement("canvas");
    // square sticker
    out.width = size;
    out.height = size;
    const ctx = out.getContext("2d");

    // Background — solid dark slate (matches brand) with rounded corners.
    const radius = Math.round(size * 0.06);
    ctx.fillStyle = "#0b1220";  // slate-950-ish
    roundRect(ctx, 0, 0, size, size, radius);
    ctx.fill();

    // Cyan corner accents
    ctx.strokeStyle = "#22D3EE";
    ctx.lineWidth = Math.max(2, Math.round(size * 0.005));
    const cl = Math.round(size * 0.05);
    drawCorner(ctx, padding, padding, cl, "tl");
    drawCorner(ctx, size - padding, padding, cl, "tr");
    drawCorner(ctx, padding, size - padding, cl, "bl");
    drawCorner(ctx, size - padding, size - padding, cl, "br");

    // White card behind QR for contrast / scannability
    const qx = Math.round((size - qrSize) / 2);
    const qy = Math.round(size * 0.16);
    const cardPad = Math.round(qrSize * 0.05);
    ctx.fillStyle = "#ffffff";
    roundRect(ctx, qx - cardPad, qy - cardPad, qrSize + cardPad * 2, qrSize + cardPad * 2, Math.round(size * 0.02));
    ctx.fill();

    // Stamp the QR canvas onto the sticker.
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(src, qx, qy, qrSize, qrSize);

    // Brand text — "NOVAGENIX LABS" in cyan label-mono style.
    const titleY = qy + qrSize + cardPad + Math.round(size * 0.07);
    ctx.fillStyle = "#22D3EE";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `600 ${Math.round(size * 0.045)}px "Liberation Sans", Arial, sans-serif`;
    ctx.fillText("NOVAGENIX LABS", size / 2, titleY);

    // Tagline / instructions
    ctx.fillStyle = "#94a3b8"; // slate-400
    ctx.font = `400 ${Math.round(size * 0.024)}px "Liberation Sans", Arial, sans-serif`;
    ctx.fillText("Scan to shop research peptides", size / 2, titleY + Math.round(size * 0.045));

    // URL text
    ctx.fillStyle = "#cbd5e1"; // slate-300
    ctx.font = `500 ${Math.round(size * 0.026)}px "Liberation Mono", monospace`;
    const urlText = finalUrl.replace(/^https?:\/\//, "");
    ctx.fillText(urlText, size / 2, titleY + Math.round(size * 0.085));

    const link = document.createElement("a");
    link.download = "novagenix-sticker.png";
    link.href = out.toDataURL("image/png");
    link.click();
    toast.success("Sticker PNG downloaded");
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="qr-page">
      <div className="label-mono text-cyan-400 mb-3">/ QUICK ACCESS</div>
      <h1 className="text-4xl md:text-5xl font-medium tracking-tight">Scan to shop</h1>
      <p className="mt-3 text-slate-400 max-w-xl">Print these QR codes on stickers for packaging, business cards, conferences, lab posters or sample kits. Customers point their phone — and land straight in your catalog.</p>

      {/* URL config */}
      <div className="mt-10 grid md:grid-cols-2 gap-4 max-w-3xl">
        <label className="block">
          <span className="label-mono text-slate-400">Destination domain</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://novagenixlabs.com.au"
            data-testid="qr-url-input"
            className="mt-1.5 w-full bg-transparent border border-white/10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 outline-none px-3 h-11 text-sm font-mono"
          />
          <p className="mt-1.5 text-[10px] text-slate-500 leading-relaxed">Use your live production URL (not the preview) so printed stickers stay valid after deployment.</p>
        </label>
        <label className="block">
          <span className="label-mono text-slate-400">Optional path</span>
          <input
            type="text"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="/shop  or  /product/retatrutide"
            data-testid="qr-path-input"
            className="mt-1.5 w-full bg-transparent border border-white/10 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 outline-none px-3 h-11 text-sm font-mono"
          />
          <p className="mt-1.5 text-[10px] text-slate-500 leading-relaxed">Send scanners straight to a category or product — leave blank for the homepage.</p>
        </label>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-10 items-start">
        {/* Live preview */}
        <div ref={previewRef} className="border border-cyan-500/20 cyan-glow p-10 bg-slate-950 flex flex-col items-center">
          <div className="bg-white p-4">
            <QRCodeCanvas
              id="ngx-qr"
              value={finalUrl}
              size={320}
              fgColor="#020617"
              bgColor="#FFFFFF"
              level="H"
              imageSettings={{
                src: LOGO_URL,
                height: 56,
                width: 56,
                excavate: true,
              }}
            />
          </div>
          <div className="mt-6 label-mono text-cyan-400 text-center">NOVAGENIX LABS</div>
          <div className="text-xs font-mono text-slate-400 mt-1 break-all text-center max-w-[300px]" data-testid="qr-final-url">{finalUrl}</div>

          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => downloadQR(1536)}
              data-testid="qr-download-btn"
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-5 py-2.5"
            >
              <Download size={14} /> Download QR (1536px)
            </button>
            <button
              onClick={downloadSticker}
              data-testid="sticker-download-btn"
              className="inline-flex items-center gap-2 border border-cyan-500/40 hover:bg-cyan-500/10 text-cyan-400 font-semibold px-5 py-2.5"
            >
              <Sticker size={14} /> Download sticker
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 text-[10px] text-slate-500">
            <span>Sticker size:</span>
            {[1024, 1536, 2048].map((px) => (
              <button
                key={px}
                onClick={() => setStickerSize(px)}
                data-testid={`sticker-size-${px}`}
                className={`px-2 py-0.5 border ${stickerSize === px ? "border-cyan-500 bg-cyan-500/10 text-cyan-400" : "border-white/10 hover:border-cyan-500/40"}`}
              >
                {px}px
              </button>
            ))}
          </div>
        </div>

        {/* Use cases + tips */}
        <div className="space-y-6">
          <div>
            <div className="label-mono text-slate-400 mb-2">/ Use cases</div>
            <ul className="space-y-3 text-slate-300">
              <li className="flex gap-3"><span className="text-cyan-400">01</span><span>Print on packaging slips so re-orders take 3 seconds.</span></li>
              <li className="flex gap-3"><span className="text-cyan-400">02</span><span>Affix to lab posters or trade-show banners.</span></li>
              <li className="flex gap-3"><span className="text-cyan-400">03</span><span>Embed on partnered researcher business cards.</span></li>
              <li className="flex gap-3"><span className="text-cyan-400">04</span><span>Display at retail kiosk / showroom for instant mobile checkout.</span></li>
            </ul>
          </div>
          <div className="border border-white/5 p-5 text-xs text-slate-400 leading-relaxed">
            <span className="label-mono text-cyan-400 block mb-2">Printing tips</span>
            <ul className="space-y-2 list-disc list-inside">
              <li>For tiny stickers (≤25mm), keep the QR ≥80% of the sticker area.</li>
              <li>Maintain at least 4mm of clear margin so phones lock on quickly.</li>
              <li>The error-correction is set to High (H) — your logo can occupy ~15% of the QR centre without breaking scanability.</li>
              <li>For matte / vinyl stickers, print at 300 DPI minimum.</li>
              <li>Test-scan from the actual sticker before doing a big run.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- canvas helpers ----------
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawCorner(ctx, x, y, len, pos) {
  ctx.beginPath();
  if (pos === "tl") { ctx.moveTo(x, y + len); ctx.lineTo(x, y); ctx.lineTo(x + len, y); }
  else if (pos === "tr") { ctx.moveTo(x - len, y); ctx.lineTo(x, y); ctx.lineTo(x, y + len); }
  else if (pos === "bl") { ctx.moveTo(x, y - len); ctx.lineTo(x, y); ctx.lineTo(x + len, y); }
  else if (pos === "br") { ctx.moveTo(x - len, y); ctx.lineTo(x, y); ctx.lineTo(x, y - len); }
  ctx.stroke();
}
